import { useMutation, useQuery } from '@apollo/client';
import React, { FC, useState } from 'react';
import { GET_GIFT_CARDS } from './graphql/queries';
import { GiftCardType } from './type';
import GiftCard from '../../Components/ApplyGiftCardSection/components/GiftCard/GiftCard';
import { Backdrop, Button, CircularProgress, Grid, makeStyles, Modal, TextField } from '@material-ui/core';
import { DELETE_GIFT_VOUCHER } from './graphql/mutations';
import { GET_CUSTOMER } from '../Customers/graphql/queries';
import useNotifications from '../../Notifications/useNotifications';
import { useNavigate } from 'react-router-dom';
import { useUpdateGiftVoucherMutation } from './graphql/updateGiftVoucher.generated';

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    paddingTop: '10rem',
    justifyContent: 'center'
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    textAlign: 'center',
    height: '25%',
    width: '50%',
    padding: theme.spacing(2, 4, 3)
  }
}));

const GiftVouchers: FC = () => {
  const [search, setSearch] = useState<string>();
  const { modal, modalPaper } = useStyles();
  const navigate = useNavigate();
  const [code, setCode] = useState<string>();
  const [amount, setAmount] = useState<number>(0);
  const { addError, addSuccess } = useNotifications();
  const { data, loading, refetch } = useQuery(GET_GIFT_CARDS, { variables: { search } });
  const [deleteGiftVoucher] = useMutation(DELETE_GIFT_VOUCHER, {
    onCompleted: ({ deleteGiftCard }) => (deleteGiftCard ? navigate(-1) : null),
    onError: () => addError('There was an error deleting this voucher. Please try again later.')
  });

  const [updateGiftCard] = useUpdateGiftVoucherMutation({
    onCompleted: () => {
      refetch();
      handleClose();
    },
    onError: () => addError('There was an error deleting this voucher. Please try again later.')
  });

  const giftCards: GiftCardType[] = (data && data.giftCards) || [];

  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      setSearch(e.target.value);
    }
  };

  const handleDelete = async (giftCard: GiftCardType) => {
    console.log({ giftCard });
    await deleteGiftVoucher({
      variables: { id: giftCard.id },
      refetchQueries: [{ query: GET_GIFT_CARDS }, { query: GET_CUSTOMER, variables: { id: giftCard.customer?.id } }]
    });
  };

  const handleUpdateGiftCard = async () => {
    await updateGiftCard({ variables: { code: code || '', amount } });
  };

  const handleClose = () => {
    setAmount(0);
    setCode(undefined);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2rem' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <h1 className="my-6 text-3xl">Gift vouchers</h1>
      <TextField
        id="inline-search"
        label="Search"
        placeholder="Search by code..."
        type="search"
        variant="outlined"
        name="search"
        onKeyPress={onKeyPress}
        style={{ width: '100%', marginBottom: '2rem' }}
        margin="normal"
      />
      <Grid container spacing={3}>
        {giftCards.map((giftCard) => {
          const details = {
            label: 'Details',
            onClick: () => navigate(`/gift-vouchers/${giftCard.id}`)
          };
          const getButtonConfig = () => {
            if (giftCard && giftCard.usedOn) {
              const map = {
                booking: `/bookings/${giftCard.usedOn.id}`,
                ticket: `/tickets/${giftCard.usedOn.id}`,
                package: `/packages/${giftCard.usedOn.id}`
              };

              return [{ label: 'See product', onClick: () => navigate(map[giftCard.usedOn.type]) }, details];
            }

            return [
              { label: 'Delete', onClick: () => handleDelete(giftCard) },
              {
                label: 'Update',
                onClick: () => {
                  setCode(giftCard.code);
                  setAmount(giftCard.amount);
                }
              },
              details
            ];
          };
          return (
            <Grid key={giftCard.id} item lg={3} md={4} xs={12}>
              <GiftCard code={giftCard.code} amount={giftCard.amount} actions={getButtonConfig()} />
            </Grid>
          );
        })}
      </Grid>
      <Modal
        className={modal}
        open={!!code}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <div className={modalPaper}>
          <h2 className="text-lg font-bold">Update gift voucher amount</h2>
          <TextField
            type="number"
            onWheel={(e) => {
              // Prevent the input value change
              (e.target as HTMLInputElement).blur();
            }}
            value={amount}
            onChange={(e) => setAmount(+e.target.value)}
            style={{ marginBottom: '1rem', width: '100%' }}
          />
          <div>
            <Button color="primary" disabled={!amount} onClick={handleUpdateGiftCard} variant="contained">
              Update
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default GiftVouchers;
